import React, { ReactElement } from 'react';
import { Card } from '../../../components/Card/Card';
import { NoData } from '../../../components/NoData/NoData';
import { graphql } from '../../../generated';
import { t } from '../../../locales/translate';
import { withSuspendedCard } from '../../../util/suspense';
import { AreaChart, Dataset } from '../../../components/Charts/AreaChart';
import { Text } from '../../../components/text/text';
import { useSuspenseQuery } from '@apollo/client';
import { SalesOverTimePeriod } from '../../../generated/graphql';
import { Icon } from '../../../components/Icon/Icon';
import { formatCurrency } from '../../../util/formatters';
import { Stack } from '../../../components/Stack/Stack';
import { Tooltip } from '../../../components/Tooltip/Tooltip';

const AVERAGE_ORDER_VALUE = graphql(`
  query AverageOrderValue($from: ISO8601DateTime, $to: ISO8601DateTime, $period: SalesOverTimePeriod) {
    statistics {
      averageOrderValueOverTime(during: { from: $from, to: $to }, period: $period) {
        total
        result {
          date
          total
        }
      }
    }
  }
`);

interface Props {
  from: Date;
  to: Date;
  period: SalesOverTimePeriod;
}

function AverageOrderValue$({ from, to, period }: Props): ReactElement {
  const { data } = useSuspenseQuery(AVERAGE_ORDER_VALUE, {
    variables: {
      from: from,
      to: to,
      period: period,
    },
  });

  const total = data?.statistics?.averageOrderValueOverTime?.total;
  const averageOrderValueOverTimeData = data?.statistics?.averageOrderValueOverTime;
  const dataset: Dataset[] = [
    {
      title: t.component.averageOrderValue.period[period],
      values: averageOrderValueOverTimeData?.result.map((e) => e.total) || [],
      color: '#00EDFF80',
    },
  ];

  return (
    <Card
      size="dashboard"
      title={
        <Stack line size="xxs">
          <p>{t.component.averageOrderValue.title}</p>
          <Tooltip small padding="medium" position="bottom" reference={<Icon name="info-circle" color="#909090" />}>
            <Text type="tooltip-content">{t.component.averageOrderValue.tooltip}</Text>
          </Tooltip>
        </Stack>
      }
      actions={
        averageOrderValueOverTimeData &&
        averageOrderValueOverTimeData.result.length > 0 && <Text type="card-label">{formatCurrency(total)}</Text>
      }
    >
      {averageOrderValueOverTimeData && averageOrderValueOverTimeData.result.length > 0 ? (
        <AreaChart
          style={{ height: 249 }}
          datasets={dataset}
          labels={averageOrderValueOverTimeData.result.map((e) => e.date)}
          formatTooltipValues="currency"
          showTimeOnXAxis={period === 'hour'}
        />
      ) : (
        <NoData description={t.component.averageOrderValue.noData} />
      )}
    </Card>
  );
}

export const AverageOrderValue = withSuspendedCard(AverageOrderValue$, {
  loadingTitle: t.component.averageOrderValue.title,
  loadingHeight: 348,
});
